import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import './lessonAdmin.css';

function authHeader() { const t = localStorage.getItem('token'); return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }; }

function Modal({ show, title, onClose, children, footer }) {
  if (!show) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h4>{title}</h4>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export default function LessonAdmin() {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const normalizeItems = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (!raw && raw !== 0) return [];
    if (typeof raw === 'string') {
      // split items by newline or comma into items (items list may be comma-separated)
      const parts = raw.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
      return parts.length ? parts : [];
    }
    // fallback: wrap in array
    return [raw];
  };

  // Outline parsing: DO NOT split outline by comma — treat newline as separator only
  const normalizeOutline = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (!raw && raw !== 0) return [];
    if (typeof raw === 'string') {
      // split only by newline; commas remain part of the item text
      const parts = raw.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
      return parts.length ? parts : [];
    }
    return [raw];
  };
  const resolveThumbnailSrc = (thumb) => {
    if (!thumb) return '';
    try {
      if (String(thumb).startsWith('data:')) return thumb; // data URL preview
      if (String(thumb).startsWith('http://') || String(thumb).startsWith('https://')) return thumb;
      // server-relative path like '/uploads/lessons/thumbnail/..' -> prefix with apiUrl
      if (String(thumb).startsWith('/')) {
        const base = apiUrl ? apiUrl.replace(/\/\/$/, '') : '';
        return base + thumb;
      }
      return thumb;
    } catch (e) { return thumb; }
  };
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', grade_id: '', content: [] });
  const [errors, setErrors] = useState({});
  const [grades, setGrades] = useState([]);
  const [expandedWeeks, setExpandedWeeks] = useState({});

  // compact react-select styles (match RSSelect behavior used elsewhere)
  const OPTION_HEIGHT = 28;
  const VISIBLE_OPTIONS = 6;
  const MENU_MAX_HEIGHT = OPTION_HEIGHT * VISIBLE_OPTIONS;
  const compactStyles = {
    control: base => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderRadius: 6, padding: '0 6px', width: 180, maxWidth: '100%' }),
    valueContainer: base => ({ ...base, padding: '0 6px', height: 32 }),
    indicatorsContainer: base => ({ ...base, height: 32 }),
    input: base => ({ ...base, margin: 0, padding: 0 }),
    singleValue: base => ({ ...base, margin: 0 }),
    placeholder: base => ({ ...base, margin: 0, color: '#666', fontSize: 13 }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    menu: base => ({ ...base, minWidth: 180, width: 180 }),
    menuList: base => ({ ...base, maxHeight: MENU_MAX_HEIGHT, overflowY: 'auto' }),
    option: base => ({ ...base, fontSize: 13, padding: '4px 8px', minHeight: OPTION_HEIGHT, display: 'flex', alignItems: 'center' })
  };

  const fetchLessons = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/lessons`, { headers: authHeader() });
      if (res.ok) {
        const jd = await res.json();
        setLessons(jd.lessons || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchLessons(); }, []);

  useEffect(() => { fetchGrades(); }, []);

  const fetchGrades = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/grades`, { headers: authHeader() });
      if (res.ok) {
        const jd = await res.json();
        setGrades(jd.grades || []);
      }
    } catch (e) { console.error(e); }
  };

  const startCreate = () => { setEditing(null); setForm({ title: '', grade_id: '', content: [] }); setErrors({}); setShowModal(true); };
  const startEdit = (l) => {
    // merge lesson.content (lesson weeks) with lesson.activities (activity weeks) so each week has topic/items
    const content = [];
    const lessonWeeks = Array.isArray(l.content) ? l.content : [];
    const activityWeeks = Array.isArray(l.activities) ? l.activities : [];
    // index activities by week for quick lookup
    const actByWeek = {};
    for (const a of activityWeeks) {
      if (a && a.week !== undefined && a.week !== null) actByWeek[String(a.week)] = a;
    }
    if (lessonWeeks.length) {
      for (const w of lessonWeeks) {
        const aw = actByWeek[String(w.week)] || {};
        content.push({
          week: w.week,
          canva_link: w.canva_link || w.slide || '',
          title: w.title || '',
          summary: w.summary || '',
          // outline: prefer explicit outline array, but fallback to legacy `topic` string if present
          // Use newline-only splitting for outline (do not split on commas)
          outline: normalizeOutline(
            Array.isArray(aw.outline) ? aw.outline : (Array.isArray(w.outline) ? w.outline : (aw.topic || w.topic || w.outline || ''))
          ),
          status: (w.status === undefined || w.status === null) ? 'active' : (w.status || 'active'),
          thumbnail: w.thumbnail || aw.thumbnail || '',
          topic: aw.topic || w.topic || '',
          items: normalizeItems(Array.isArray(aw.items) ? aw.items : (Array.isArray(w.items) ? w.items : (w.items || '')))
        });
      }
    } else if (activityWeeks.length) {
      // no lesson weeks, but there are activities -> use activities as base
      for (const a of activityWeeks) {
        content.push({ week: a.week, canva_link: '', title: '', summary: '', status: (a.status === undefined || a.status === null) ? 'active' : (a.status || 'active'), thumbnail: a.thumbnail || '', topic: a.topic || '', items: normalizeItems(a.items) });
      }
    }
    setEditing(l.id);
    setForm({ title: l.title || '', grade_id: l.grade_id || '', content });
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.title || String(form.title).trim().length === 0) e.title = 'Title is required';
    if (!form.grade_id) e.grade_id = 'Select a grade';
    // content can be empty, but if present validate each week
    if (Array.isArray(form.content)) {
      form.content.forEach((w, idx) => {
        if (!w.week) e[`content_week_${idx}`] = 'Week number is required';
        if (!w.title || String(w.title).trim().length === 0) e[`content_title_${idx}`] = 'Week title is required';
        if (!w.summary || String(w.summary).trim().length === 0) e[`content_summary_${idx}`] = 'Summary is required';
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    try {
      // before sending to server, ensure legacy `topic` field is set from outline (backend expects topic)
      const payload = {
        ...form, content: (form.content || []).map(w => {
          const outlineArr = Array.isArray(w.outline) ? w.outline : [];
          const topicFromOutline = outlineArr.length ? outlineArr.join('\n') : (w.topic || '');
          return { ...w, topic: topicFromOutline };
        })
      };

      if (editing) {
        const res = await fetch(`${apiUrl}/api/admin/lessons/${editing}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok) { await fetchLessons(); setShowModal(false); }
      } else {
        const res = await fetch(`${apiUrl}/api/admin/lessons`, { method: 'POST', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok) { await fetchLessons(); setShowModal(false); }
      }
    } catch (e) { console.error(e); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this lesson?')) return;
    try {
      const res = await fetch(`${apiUrl}/api/admin/lessons/${id}`, { method: 'DELETE', headers: authHeader() });
      if (res.ok) fetchLessons();
    } catch (e) { console.error(e); }
  };

  const addWeek = () => {
    setForm(f => ({ ...f, content: [...(f.content || []), { week: (f.content || []).length + 1, canva_link: '', title: '', summary: '', outline: [], topic: '', items: [], status: 'active', thumbnail: '' }] }));
  };

  const updateWeek = (idx, patch) => {
    setForm(f => {
      const c = Array.isArray(f.content) ? [...f.content] : [];
      c[idx] = { ...c[idx], ...patch };
      return { ...f, content: c };
    });
  };

  const removeWeek = (idx) => {
    setForm(f => {
      const c = Array.isArray(f.content) ? [...f.content] : [];
      c.splice(idx, 1);
      return { ...f, content: c };
    });
  };

  const handleThumbnailFile = async (idx, file) => {
    if (!file) return;
    // show a temporary preview using dataURL while uploading
    const reader = new FileReader();
    reader.onload = () => {
      updateWeek(idx, { thumbnail: reader.result, uploading: true });
    };
    reader.onerror = (e) => { console.error('Failed to read thumbnail', e); };
    reader.readAsDataURL(file);

    try {
      const formData = new FormData();
      formData.append('thumbnail', file);
      const res = await fetch(`${apiUrl}/api/admin/upload/lesson-thumbnail`, { method: 'POST', headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '' }, body: formData });
      if (!res.ok) {
        console.error('Upload failed', res.status);
        updateWeek(idx, { uploading: false });
        return;
      }
      const jd = await res.json();
      // server returns { url: '/uploads/lessons/thumbnail/filename' }
      updateWeek(idx, { thumbnail: jd.url, uploading: false });
    } catch (e) {
      console.error('Upload error', e);
      updateWeek(idx, { uploading: false });
    }
  };

  // Activity items helpers: treat items as array of strings
  const addActivityItem = (weekIdx) => {
    setForm(f => {
      const c = Array.isArray(f.content) ? [...f.content] : [];
      const items = Array.isArray(c[weekIdx].items) ? [...c[weekIdx].items] : normalizeItems(c[weekIdx].items);
      items.push('');
      c[weekIdx] = { ...c[weekIdx], items };
      return { ...f, content: c };
    });
  };

  const updateActivityItem = (weekIdx, itemIdx, value) => {
    setForm(f => {
      const c = Array.isArray(f.content) ? [...f.content] : [];
      const items = Array.isArray(c[weekIdx].items) ? [...c[weekIdx].items] : normalizeItems(c[weekIdx].items);
      items[itemIdx] = value;
      c[weekIdx] = { ...c[weekIdx], items };
      return { ...f, content: c };
    });
  };

  const removeActivityItem = (weekIdx, itemIdx) => {
    setForm(f => {
      const c = Array.isArray(f.content) ? [...f.content] : [];
      const items = Array.isArray(c[weekIdx].items) ? [...c[weekIdx].items] : normalizeItems(c[weekIdx].items);
      items.splice(itemIdx, 1);
      c[weekIdx] = { ...c[weekIdx], items };
      return { ...f, content: c };
    });
  };

  // Outline items helpers (list of strings per week)
  const addOutlineItem = (weekIdx) => {
    setForm(f => {
      const c = Array.isArray(f.content) ? [...f.content] : [];
      const outline = Array.isArray(c[weekIdx].outline) ? [...c[weekIdx].outline] : [];
      outline.push('');
      c[weekIdx] = { ...c[weekIdx], outline };
      return { ...f, content: c };
    });
  };

  const updateOutlineItem = (weekIdx, itemIdx, value) => {
    setForm(f => {
      const c = Array.isArray(f.content) ? [...f.content] : [];
      const outline = Array.isArray(c[weekIdx].outline) ? [...c[weekIdx].outline] : [];
      outline[itemIdx] = value;
      c[weekIdx] = { ...c[weekIdx], outline };
      return { ...f, content: c };
    });
  };

  const removeOutlineItem = (weekIdx, itemIdx) => {
    setForm(f => {
      const c = Array.isArray(f.content) ? [...f.content] : [];
      const outline = Array.isArray(c[weekIdx].outline) ? [...c[weekIdx].outline] : [];
      outline.splice(itemIdx, 1);
      c[weekIdx] = { ...c[weekIdx], outline };
      return { ...f, content: c };
    });
  };

  return (
    <div className="admin-page">
      <div className="panel-header">
        <h2>Manage Lessons</h2>
        <div className="panel-actions">
          <button onClick={fetchLessons}>Refresh</button>
          <button onClick={startCreate}>Create new lesson</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="muted">Loading...</div> : (
          <table className="data-table">
            <thead><tr><th>No.</th><th>Year</th><th>Title</th><th>Grade</th><th>Weeks</th><th>Actions</th></tr></thead>
            <tbody>
              {(() => {
                // sort newest-first (assume numeric id increasing) and number newest as largest
                const sorted = (lessons || []).slice().sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
                const total = (sorted || []).length || 0;
                return sorted.map((l, idx) => (
                  <tr key={l.id}>
                    <td>{total - idx}</td>
                    <td>2025-2026</td>
                    <td>{l.title}</td>
                    <td>{l.grade_name || l.grade_id || '—'}</td>
                    <td>
                      {(() => {
                        const list = l.content || [];
                        const activeCount = list.filter(w => w && w.status !== 'inactive').length;
                        const inactiveCount = list.filter(w => w && w.status === 'inactive').length;
                        return (
                          <>
                            <span className='success-text'>Active: {activeCount}</span><br />
                            <span className='reject-text'>Inactive: {inactiveCount}</span>
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      <button className="icon-btn mr-10" title="Edit lesson" aria-label="Edit lesson" onClick={() => startEdit(l)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                      </button>
                      <button className="icon-btn" title="Delete lesson" aria-label="Delete lesson" onClick={() => del(l.id)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                      </button>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        show={showModal}
        title={editing ? 'Edit lesson' : 'Create lesson'}
        onClose={() => { setShowModal(false); setEditing(null); setErrors({}); }}
        footer={(
          <div className="form-actions">
            <button className="btn-primary" onClick={save}>{editing ? 'Update' : 'Save'}</button>
            <button onClick={() => { setShowModal(false); setEditing(null); }}>Cancel</button>
          </div>
        )}
      >
        <div className="modal-form">
          <div className="field">
            <label>Title</label>
            <input value={form.title} onChange={e => { setForm({ ...form, title: e.target.value }); if (errors.title) setErrors(prev => { const p = { ...prev }; delete p.title; return p; }); }} />
            {errors.title && <div className="error">{errors.title}</div>}
          </div>
          <div className="field">
            <label>Grade</label>
            <Select
              options={[{ value: '', label: '-- Select grade --' }].concat((grades || []).map(g => ({ value: g.id, label: g.name })))}
              value={(grades || []).find(g => String(g.id) === String(form.grade_id)) ? { value: form.grade_id, label: ((grades || []).find(g => String(g.id) === String(form.grade_id)) || {}).name } : null}
              onChange={opt => { const v = opt ? opt.value : ''; setForm({ ...form, grade_id: v }); if (errors.grade_id) setErrors(prev => { const p = { ...prev }; delete p.grade_id; return p; }); }}
              isClearable={true}
              placeholder="-- Select grade --"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              menuPosition={'fixed'}
              menuMaxHeight={MENU_MAX_HEIGHT}
              styles={compactStyles}
              components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
            />
            {errors.grade_id && <div className="error">{errors.grade_id}</div>}
          </div>

          <div className="field full">
            <label>Content (weeks)</label>
            {(form.content || []).map((w, idx) => {
              const key = w.week || idx;
              const expanded = !!expandedWeeks[key];
              return (
                <div key={idx} className="week-row">
                  <div className="week-panel">
                    <div className="week-header" onClick={() => setExpandedWeeks(prev => ({ ...prev, [key]: !prev[key] }))}>
                      <div>
                        Week {w.week || idx + 1} — {w.title || 'No title'}
                        {/* show status label at end of line for quick visibility */}
                        <span style={{ marginLeft: 8 }} className={((w && w.status) || 'active') === 'inactive' ? 'reject-text' : 'success-text'}>
                          {(((w && w.status) || 'active').charAt(0).toUpperCase() + ((w && w.status) || 'active').slice(1))}
                        </span>
                      </div>
                      <div>{expanded ? '▾' : '▸'}</div>
                    </div>
                    {expanded && (
                      <div className="week-body">
                        <div className="week-block">
                          <div className="block-title">Lesson</div>
                          <div className="block-field">
                            <label>Week</label>
                            <input className="small" value={w.week || ''} onChange={e => updateWeek(idx, { week: e.target.value })} />
                          </div>
                          <div className="block-field">
                            <label>Week title</label>
                            <input placeholder="Week title" value={w.title || ''} onChange={e => updateWeek(idx, { title: e.target.value })} />
                          </div>
                          <div className="block-field">
                            <label>Canva link</label>
                            <input placeholder="Canva link" value={w.canva_link || ''} onChange={e => updateWeek(idx, { canva_link: e.target.value })} />
                          </div>
                          <div className="block-field">
                            <label>Summary</label>
                            <textarea className='multi-l-t-ad' placeholder="Summary (lesson description)" value={w.summary || ''} onChange={e => updateWeek(idx, { summary: e.target.value })} />
                          </div>
                          <div className="block-field">
                            <label>Status</label>
                            <Select
                              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                              value={{ value: w.status || 'active', label: ((w.status || 'active') === 'active' ? 'Active' : 'Inactive') }}
                              onChange={opt => updateWeek(idx, { status: opt ? opt.value : 'active' })}
                              isClearable={false}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                              menuPosition={'fixed'}
                              menuMaxHeight={MENU_MAX_HEIGHT}
                              styles={compactStyles}
                              components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                            />
                          </div>
                          <div className="block-field">
                            <label>Thumbnail</label>
                            <input type="file" accept="image/*" onChange={e => { const f = e.target.files && e.target.files[0]; handleThumbnailFile(idx, f); }} />
                            {w.thumbnail ? (
                              <div style={{ marginTop: 8 }}>
                                <div className="muted">Preview:</div>
                                <img src={resolveThumbnailSrc(w.thumbnail)} alt={`week-${w.week || idx}-thumb`} style={{ maxWidth: 240, maxHeight: 135, display: 'block', marginTop: 6 }} />
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="week-block">
                          <div className="block-title">Outline</div>
                          <div className="block-field">
                            <div className="activity-items">
                              {(Array.isArray(w.outline) ? w.outline : []).map((it, i) => (
                                <div key={i} className="activity-item-row">
                                  <input placeholder={`Outline item ${i + 1}`} value={it || ''} onChange={e => updateOutlineItem(idx, i, e.target.value)} />
                                  <button className="danger small" onClick={() => removeOutlineItem(idx, i)} type="button">✕</button>
                                </div>
                              ))}
                              <div style={{ marginTop: 6 }}>
                                <button onClick={() => addOutlineItem(idx)} type="button">Add item</button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="week-block">
                          <div className="block-title">Prepared Supplies</div>
                          <div className="block-field">
                            <div className="activity-items">
                              {(Array.isArray(w.items) ? w.items : normalizeItems(w.items)).map((it, i) => (
                                <div key={i} className="activity-item-row">
                                  <input placeholder={`Item ${i + 1}`} value={it || ''} onChange={e => updateActivityItem(idx, i, e.target.value)} />
                                  <button className="danger small" onClick={() => removeActivityItem(idx, i)} type="button">✕</button>
                                </div>
                              ))}
                              <div style={{ marginTop: 6 }}>
                                <button onClick={() => addActivityItem(idx)} type="button">Add item</button>
                              </div>
                            </div>
                          </div>
                          <div className="week-actions">
                            <button className="icon-btn small" title="Delete week" aria-label="Delete week" onClick={() => removeWeek(idx)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {errors[`content_week_${idx}`] && <div className="error">{errors[`content_week_${idx}`]}</div>}
                  {errors[`content_title_${idx}`] && <div className="error">{errors[`content_title_${idx}`]}</div>}
                  {errors[`content_summary_${idx}`] && <div className="error">{errors[`content_summary_${idx}`]}</div>}
                </div>
              );
            })}
            <div>
              <button onClick={addWeek}>Add week</button>
            </div>
          </div>

        </div>
      </Modal>
    </div>
  );
}
